// Mock API 工具函数 - 用于替代后端 API 调用

export interface MockResponse<T = any> {
  data: T
  total?: number
  page?: number
  limit?: number
}

// 模拟延迟
function mockDelay(ms = 300): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// 生成随机 ID
function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

// Mock FAQ 数据
export const mockFaqs = [
  {
    id: '1',
    question: '如何开始使用这个系统？',
    answer: '您可以通过注册账户开始使用，然后完成基本设置即可。',
    category: '基础使用',
    created_at: new Date('2024-01-15'),
    updated_at: new Date('2024-01-15'),
  },
  {
    id: '2',
    question: '如何重置密码？',
    answer: '在登录页面点击"忘记密码"，然后按照邮件指引重置。',
    category: '账户管理',
    created_at: new Date('2024-01-16'),
    updated_at: new Date('2024-01-16'),
  },
  {
    id: '3',
    question: '支持哪些支付方式？',
    answer: '我们支持信用卡、PayPal、银行转账等多种支付方式。',
    category: '支付相关',
    created_at: new Date('2024-01-17'),
    updated_at: new Date('2024-01-17'),
  },
]

// Mock 通知数据
export const mockNotifications = [
  {
    id: '1',
    title: '系统更新通知',
    message: '系统将于明日凌晨2点进行维护更新，预计用时2小时。',
    link: '/notifications/1',
    read_at: null,
    created_at: new Date('2024-01-20'),
  },
  {
    id: '2',
    title: '新功能上线',
    message: '我们新增了数据导出功能，现在您可以导出所有数据。',
    link: '/features/export',
    read_at: new Date('2024-01-19'),
    created_at: new Date('2024-01-19'),
  },
  {
    id: '3',
    title: '账户验证提醒',
    message: '请完成邮箱验证以确保账户安全。',
    link: '/settings/verification',
    read_at: null,
    created_at: new Date('2024-01-18'),
  },
]

// Mock 快捷方式数据
export const mockShortcuts = [
  {
    id: '1',
    route: '/dashboard',
    title: '仪表板',
    icon: 'ri-dashboard-line',
    user_id: 'user1',
    created_at: new Date('2024-01-15'),
  },
  {
    id: '2',
    route: '/settings',
    title: '设置',
    icon: 'ri-settings-line',
    user_id: 'user1',
    created_at: new Date('2024-01-16'),
  },
]

// Mock 订阅数据
export const mockSubscriptionData = {
  customer: {
    id: 'cus_mock123',
    email: 'user@example.com',
    name: 'Mock User',
  },
  subscription: {
    id: 'sub_mock123',
    status: 'active',
    current_period_start: Math.floor(Date.now() / 1000),
    current_period_end: Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000),
    plan: {
      id: 'plan_mock123',
      nickname: 'Professional Plan',
      amount: 2999,
      currency: 'usd',
      interval: 'month',
    },
  },
  subscriptions: [],
}

// Mock API 工具类
export class MockApiClient {
  // FAQ APIs
  static async getFaqs(query?: any): Promise<MockResponse> {
    await mockDelay()
    return {
      data: mockFaqs,
      total: mockFaqs.length,
      page: query?.page || 1,
      limit: query?.limit || 10,
    }
  }

  // 通知 APIs
  static async getNotifications(userId: string, query?: any): Promise<MockResponse> {
    await mockDelay()
    return {
      data: mockNotifications,
      total: mockNotifications.length,
      page: query?.page || 1,
      limit: query?.limit || 10,
    }
  }

  static async markNotificationRead(userId: string, notificationId: string): Promise<any> {
    await mockDelay()
    const notification = mockNotifications.find(n => n.id === notificationId)
    if (notification) {
      notification.read_at = new Date()
    }
    return { success: true }
  }

  static async markNotificationUnread(userId: string, notificationId: string): Promise<any> {
    await mockDelay()
    const notification = mockNotifications.find(n => n.id === notificationId)
    if (notification) {
      notification.read_at = null
    }
    return { success: true }
  }

  static async markAllNotificationsRead(userId: string): Promise<any> {
    await mockDelay()
    mockNotifications.forEach(n => n.read_at = new Date())
    return { success: true }
  }

  static async markAllNotificationsUnread(userId: string): Promise<any> {
    await mockDelay()
    mockNotifications.forEach(n => n.read_at = null)
    return { success: true }
  }

  static async deleteNotification(userId: string, notificationId: string): Promise<any> {
    await mockDelay()
    const index = mockNotifications.findIndex(n => n.id === notificationId)
    if (index > -1) {
      mockNotifications.splice(index, 1)
    }
    return { success: true }
  }

  static async countUnreadNotifications(userId: string): Promise<{ total: number }> {
    await mockDelay()
    const total = mockNotifications.filter(n => !n.read_at).length
    return { total }
  }

  // 快捷方式 APIs
  static async getShortcuts(userId: string): Promise<MockResponse> {
    await mockDelay()
    return {
      data: mockShortcuts.filter(s => s.user_id === userId),
      total: mockShortcuts.length,
    }
  }

  static async createShortcut(userId: string, route: string): Promise<any> {
    await mockDelay()
    const newShortcut = {
      id: generateId(),
      route,
      title: route.split('/').pop() || 'Unknown',
      icon: 'ri-bookmark-line',
      user_id: userId,
      created_at: new Date(),
    }
    mockShortcuts.push(newShortcut)
    return { data: newShortcut }
  }

  static async deleteShortcut(userId: string, shortcutId: string): Promise<any> {
    await mockDelay()
    const index = mockShortcuts.findIndex(s => s.id === shortcutId && s.user_id === userId)
    if (index > -1) {
      const deleted = mockShortcuts.splice(index, 1)
      return { data: deleted }
    }
    return { data: [] }
  }

  // 订阅 APIs
  static async getSubscription(): Promise<any> {
    await mockDelay()
    return mockSubscriptionData
  }

  static async createCheckoutUrl(customerId: string, priceId: string): Promise<{ url: string }> {
    await mockDelay()
    return {
      url: `https://checkout.stripe.com/mock/${customerId}/${priceId}`,
    }
  }

  // 健康检查
  static async healthCheck(): Promise<{ success: boolean }> {
    await mockDelay(100) // 快速响应
    return { success: true }
  }
}
